import React, { useEffect, useState } from 'react';
import { Tag, Percent, IndianRupee, Save, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_CODES = ['basic', 'standard', 'premium'];

const PublicPlanDiscounts = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCode, setSavingCode] = useState(null);
  const [message, setMessage] = useState(null);
  const token = localStorage.getItem('token');

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/subscriptions/plans-with-discounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = (res.data || []).filter(p => PUBLIC_CODES.includes(p.code));
      const order = { basic: 1, standard: 2, premium: 3 };
      data.sort((a, b) => (order[a.code] || 99) - (order[b.code] || 99));
      setPlans(data.map(p => ({
        ...p,
        discount_enabled: !!p.discount_enabled,
        discount_type: p.discount_type || 'percent',
        discount_value_monthly: p.discount_value_monthly ?? p.discount_value ?? 0,
        discount_value_yearly: p.discount_value_yearly ?? p.discount_value ?? 0,
        discount_label: p.discount_label || ''
      })));
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to load plans' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const handleChange = (code, field, value) => {
    setPlans(plans.map(p => p.code === code ? { ...p, [field]: value } : p));
  };

  const handleSave = async (plan) => {
    setSavingCode(plan.code);
    setMessage(null);
    try {
      const payload = {
        discount_enabled: plan.discount_enabled,
        discount_type: plan.discount_type,
        discount_value_monthly: parseFloat(plan.discount_value_monthly) || 0,
        discount_value_yearly: parseFloat(plan.discount_value_yearly) || 0,
        discount_label: plan.discount_label || ''
      };
      await axios.put(`${API}/api/subscriptions/plans/${plan.code}/discount`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: `${plan.name} discount updated` });
      setTimeout(() => setMessage(null), 3000);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Save failed' });
    } finally {
      setSavingCode(null);
    }
  };

  const computePreview = (plan, cycle) => {
    const base = cycle === 'yearly' ? (plan.price_yearly || plan.amount_yearly || 0) : (plan.price_monthly || plan.amount_monthly || 0);
    const dVal = parseFloat(cycle === 'yearly' ? plan.discount_value_yearly : plan.discount_value_monthly) || 0;
    const dAmt = plan.discount_enabled && dVal > 0
      ? (plan.discount_type === 'percent' ? Math.round(base * (dVal / 100)) : Math.min(dVal, base))
      : 0;
    const after = Math.max(0, base - dAmt);
    const gst = Math.round(after * 0.18);
    return { base, dAmt, after, gst, total: after + gst };
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#0a1628]" size={28} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="discount-manager-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#0a1628] flex items-center gap-2"><Tag size={26} /> Public Plan Discounts</h1>
        <p className="text-sm text-gray-500 mt-1">Set or toggle discounts for Basic, Standard, Premium plans shown on the public Purchase page.</p>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`} data-testid="discount-flash">
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map(plan => {
          const monthly = computePreview(plan, 'monthly');
          const yearly = computePreview(plan, 'yearly');
          return (
            <div key={plan.code} className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm" data-testid={`discount-card-${plan.code}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-[#0a1628]">{plan.name}</h3>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={plan.discount_enabled}
                    onChange={e => handleChange(plan.code, 'discount_enabled', e.target.checked)}
                    data-testid={`discount-toggle-${plan.code}`}
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-checked:bg-[#dc2626] rounded-full peer transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
                  <span className="ml-2 text-sm text-gray-700">{plan.discount_enabled ? 'ON' : 'OFF'}</span>
                </label>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500">Discount Type</label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => handleChange(plan.code, 'discount_type', 'percent')}
                      className={`flex items-center justify-center gap-1 py-2 rounded-lg border text-sm transition ${plan.discount_type === 'percent' ? 'bg-[#0a1628] border-[#0a1628] text-white' : 'bg-white border-gray-200 text-gray-600'}`}
                      data-testid={`type-percent-${plan.code}`}
                    ><Percent size={14} /> Percent</button>
                    <button
                      type="button"
                      onClick={() => handleChange(plan.code, 'discount_type', 'flat')}
                      className={`flex items-center justify-center gap-1 py-2 rounded-lg border text-sm transition ${plan.discount_type === 'flat' ? 'bg-[#0a1628] border-[#0a1628] text-white' : 'bg-white border-gray-200 text-gray-600'}`}
                      data-testid={`type-flat-${plan.code}`}
                    ><IndianRupee size={14} /> Flat</button>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-500">Monthly Discount {plan.discount_type === 'percent' ? '(%)' : '(₹)'}</label>
                  <input
                    type="number"
                    min="0"
                    value={plan.discount_value_monthly}
                    onChange={e => handleChange(plan.code, 'discount_value_monthly', e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#0a1628] outline-none"
                    data-testid={`discount-monthly-${plan.code}`}
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500">Yearly Discount {plan.discount_type === 'percent' ? '(%)' : '(₹)'}</label>
                  <input
                    type="number"
                    min="0"
                    value={plan.discount_value_yearly}
                    onChange={e => handleChange(plan.code, 'discount_value_yearly', e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#0a1628] outline-none"
                    data-testid={`discount-yearly-${plan.code}`}
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500">Label (optional, eg: "Holi Special")</label>
                  <input
                    type="text"
                    value={plan.discount_label}
                    onChange={e => handleChange(plan.code, 'discount_label', e.target.value)}
                    placeholder="Special Offer"
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#0a1628] outline-none"
                    data-testid={`discount-label-${plan.code}`}
                  />
                </div>

                {plan.discount_enabled && (
                  <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-xs space-y-2">
                    <div className="font-semibold text-[#0a1628] mb-1">Live Preview (after GST 18%):</div>
                    <div>
                      <div className="text-gray-500">Monthly</div>
                      <div className="flex justify-between"><span className="text-gray-400 line-through">₹{monthly.base.toLocaleString('en-IN')}</span><span className="text-red-600">− ₹{monthly.dAmt.toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between font-bold text-[#0a1628]"><span>Total payable</span><span>₹{monthly.total.toLocaleString('en-IN')}</span></div>
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <div className="text-gray-500">Yearly</div>
                      <div className="flex justify-between"><span className="text-gray-400 line-through">₹{yearly.base.toLocaleString('en-IN')}</span><span className="text-red-600">− ₹{yearly.dAmt.toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between font-bold text-[#0a1628]"><span>Total payable</span><span>₹{yearly.total.toLocaleString('en-IN')}</span></div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleSave(plan)}
                  disabled={savingCode === plan.code}
                  className="w-full py-2.5 rounded-lg bg-[#dc2626] hover:bg-[#b91c1c] text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid={`discount-save-${plan.code}`}
                >
                  {savingCode === plan.code ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                  Save {plan.name} Discount
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PublicPlanDiscounts;
